// import { ChapterNode, ContentData, IndexNode } from './types-v3'
import { ContentDataType, ChapterNode, IndexNode, UISchema, FieldDefinition } from './types-v6'
import { JsonFormPropertyV2, JsonFormSchemaV2 } from '../FormComponent/types'

/**
 * Interface for ContentData with required position information
 */
// export interface ContentDataWithPosition extends ContentData {
export interface ContentDataWithPosition extends ContentDataType {
  position: string[] // Make position required
}

/**
 * Builds a map of content IDs to their positions in the document hierarchy
 * @param indexData The hierarchical index structure of the document
 * @returns A mapping of content IDs to their position arrays
 */
function buildContentPositionMap(indexData: ChapterNode[]): Record<string, string[]> {
  const contentPositionMap: Record<string, string[]> = {}

  // Recursive function to traverse the hierarchy
  function traverseIndex(node: IndexNode, positionPath: string[] = []) {
    const currentPath = [...positionPath, node.title]

    // Process contentRefs if they exist at this level
    if ('contentRefs' in node && node.contentRefs) {
      node.contentRefs.forEach((contentId) => {
        contentPositionMap[contentId] = currentPath
      })
    }

    // Process children recursively
    if ('children' in node && node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        traverseIndex(child, currentPath)
      })
    }
  }

  // Process each top-level chapter
  indexData.forEach((chapter) => {
    traverseIndex(chapter)
  })

  return contentPositionMap
}

/**
 * Adds position information to content blocks based on their location in the document hierarchy
 * @param contentBlocks Array of content blocks
 * @param indexData The hierarchical index structure of the document
 * @returns An array of content blocks with position information added
 */
export function addPositionToContentBlocks(
  // contentBlocks: ContentData[],
  contentBlocks: ContentDataType[],
  indexData: ChapterNode[]
): ContentDataWithPosition[] {
  // Build the content position map
  const contentPositionMap = buildContentPositionMap(indexData)

  // Add position property to each content object
  return contentBlocks.map((content) => {
    // Check if position exists and is an array, otherwise get from map or use empty array
    const existingPosition = content.position
    const position = Array.isArray(existingPosition) ? existingPosition : contentPositionMap[content.id] || []

    return {
      ...content,
      position,
    } as ContentDataWithPosition
  })
}

/**
 * Converts a UISchema to a JsonFormSchemaV2
 * @param uiSchema The UI schema to convert
 * @param defaultValues Optional default values for the form fields
 * @returns A JsonFormSchemaV2 compatible with the FormComponent
 */
export function uiSchemaToFormSchema(
  uiSchema: UISchema | FieldDefinition,
  defaultValues?: ContentDataType
): JsonFormSchemaV2 {
  if (!uiSchema.fields) {
    throw new Error('Fields are required')
  }
  // Extract fields from the UI schema
  const editableFields = uiSchema.fields.filter((field: FieldDefinition) => {
    if (field.fields && field.fields.length) {
      return false
    }
    return field.fieldConfig.editing.editable !== false
  })

  // Convert UI schema fields to form properties
  const formProperties: JsonFormPropertyV2[] = editableFields
    .map((field: FieldDefinition) => {
      const editing = field.fieldConfig.editing

      // Create the form property from the field definition
      const formProperty: JsonFormPropertyV2 = {
        id: field.propertyId,
        title: field.title,
        type: editing.type || 'string',
        description: field.description,
        readOnly: editing.readOnly || false,
        widget: editing.widget,
        items: editing.items,
        choices: editing.choices,
        format: editing.format,
        minimum: editing.minimum,
        maximum: editing.maximum,
        minLength: editing.minLength,
        maxLength: editing.maxLength,
        pattern: editing.pattern,
        options: editing.options,
        fieldLabels: editing.fieldLabels,
        isMultiSelect: editing.isMultiSelect,
        requireWhen: editing.requireWhen,
        validation: editing.validation,
        visible: editing.visible,
        required: editing.required,
        statusIndicator: editing.statusIndicator,
      }

      // Add default value if provided
      if (defaultValues && defaultValues[field.propertyId] !== undefined) {
        formProperty.default = defaultValues[field.propertyId]
      } else if (editing.default !== undefined) {
        formProperty.default = editing.default
      }

      return formProperty
    })
    .filter(Boolean) as JsonFormPropertyV2[]

  // Get required fields
  const requiredFields = editableFields
    .filter((field: FieldDefinition) => field.fieldConfig.editing.required)
    .map((field: FieldDefinition) => field.propertyId)

  // Create the form schema
  const formSchema: JsonFormSchemaV2 = {
    type: 'object',
    title: uiSchema.title,
    description: uiSchema.description,
    properties: formProperties,
    required: requiredFields.length > 0 ? requiredFields : undefined,
  }

  return formSchema
}
